import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { alertService } from '../services/api';
import type { AlertState, Alert, AlertFilters } from '../types';

// Initial state
const initialState: AlertState = {
  alerts: [],
  unreadCount: 0,
  loading: false,
  error: null,
};

// Async thunks for alerts
export const fetchAlerts = createAsyncThunk(
  'alerts/fetchAlerts',
  async (params: AlertFilters = {}, { rejectWithValue }) => {
    try {
      const response = await alertService.getAll(params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch alerts');
    }
  },
  {
    // Avoid duplicate in-flight requests (e.g. React Strict Mode dev double-mount, Layout + page).
    condition: (_, { getState }) => {
      const state = getState() as unknown as { alerts: Pick<AlertState, 'loading'> };
      return !state.alerts.loading;
    },
  }
);

export const markAlertAsRead = createAsyncThunk(
  'alerts/markAlertAsRead',
  async (id: number, { rejectWithValue }) => {
    try {
      await alertService.markAsRead(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to mark alert as read');
    }
  }
);

export const markAlertAsResolved = createAsyncThunk(
  'alerts/markAlertAsResolved',
  async (id: number, { rejectWithValue }) => {
    try {
      await alertService.markAsResolved(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to mark alert as resolved');
    }
  }
);

// Helper function to count unread alerts with safety checks
const countUnreadAlerts = (alerts: Alert[] | undefined | null): number => {
  if (!alerts || !Array.isArray(alerts)) {
    return 0;
  }
  return alerts.filter(alert => alert && !alert.is_read).length;
};

// Alerts slice
const alertsSlice = createSlice({
  name: 'alerts',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    addAlert: (state, action: PayloadAction<Alert>) => {
      // Ensure alerts is an array
      if (!Array.isArray(state.alerts)) {
        state.alerts = [];
      }
      state.alerts.unshift(action.payload);
      if (!action.payload.is_read) {
        state.unreadCount += 1;
      }
    },
    removeAlert: (state, action: PayloadAction<number>) => {
      // Ensure alerts is an array
      if (!Array.isArray(state.alerts)) {
        state.alerts = [];
        return;
      }
      const alertIndex = state.alerts.findIndex(alert => alert && alert.id === action.payload);
      if (alertIndex !== -1) {
        const alert = state.alerts[alertIndex];
        if (alert && !alert.is_read) {
          state.unreadCount -= 1;
        }
        state.alerts.splice(alertIndex, 1);
      }
    },
    markLocalAlertAsRead: (state, action: PayloadAction<number>) => {
      // Ensure alerts is an array
      if (!Array.isArray(state.alerts)) {
        state.alerts = [];
        return;
      }
      const alert = state.alerts.find(alert => alert && alert.id === action.payload);
      if (alert && !alert.is_read) {
        alert.is_read = true;
        state.unreadCount -= 1;
      }
    },
    markLocalAlertAsResolved: (state, action: PayloadAction<number>) => {
      // Ensure alerts is an array
      if (!Array.isArray(state.alerts)) {
        state.alerts = [];
        return;
      }
      const alert = state.alerts.find(alert => alert && alert.id === action.payload);
      if (alert) {
        alert.is_resolved = true;
      }
    },
    clearAllAlerts: (state) => {
      state.alerts = [];
      state.unreadCount = 0;
    },
    resetAlertsState: (state) => {
      state.alerts = [];
      state.unreadCount = 0;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch alerts
    builder
      .addCase(fetchAlerts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAlerts.fulfilled, (state, action) => {
        state.loading = false;
        // Ensure payload is valid and is an array
        const alertsData = action.payload;
        if (alertsData && Array.isArray(alertsData.data)) {
          state.alerts = alertsData.data;
          state.unreadCount = countUnreadAlerts(alertsData.data);
        } else if (Array.isArray(alertsData)) {
          state.alerts = alertsData;
          state.unreadCount = countUnreadAlerts(alertsData);
        } else {
          state.alerts = [];
          state.unreadCount = 0;
        }
      })
      .addCase(fetchAlerts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        // Ensure alerts remains an array even on error
        if (!Array.isArray(state.alerts)) {
          state.alerts = [];
        }
      })

    // Mark alert as read
    builder
      .addCase(markAlertAsRead.fulfilled, (state, action) => {
        if (!Array.isArray(state.alerts)) {
          state.alerts = [];
          return;
        }
        const alert = state.alerts.find(alert => alert && alert.id === action.payload);
        if (alert && !alert.is_read) {
          alert.is_read = true;
          state.unreadCount -= 1;
        }
      })
      .addCase(markAlertAsRead.rejected, (state, action) => {
        state.error = action.payload as string;
      })

    // Mark alert as resolved
    builder
      .addCase(markAlertAsResolved.fulfilled, (state, action) => {
        if (!Array.isArray(state.alerts)) {
          state.alerts = [];
          return;
        }
        const alert = state.alerts.find(alert => alert && alert.id === action.payload);
        if (alert) {
          alert.is_resolved = true;
        }
      })
      .addCase(markAlertAsResolved.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  addAlert,
  removeAlert,
  markLocalAlertAsRead,
  markLocalAlertAsResolved,
  clearAllAlerts,
  resetAlertsState,
} = alertsSlice.actions;

export default alertsSlice.reducer; 